import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Button, Checkbox, Box, Select, MenuItem, IconButton, Paper, FormControlLabel, Radio, RadioGroup, Collapse, Grid, Autocomplete, TextField, CircularProgress, Stack, FormControl, InputLabel, Chip, styled } from '@mui/material';
// icons
import SearchIcon from '@mui/icons-material/Search';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
// contexts
import { CardFilterContext, defaultCardFilter } from 'contexts/cardFilter';
import { CardTypesContext } from 'contexts/cardTypes';
// API
import { CardFilter, CardType, FuzzySearchResult, statusRange, orderKeys, Order, fetchCardNames } from 'api/card';
// components
import { cardSymbol, SymbolButton } from 'components/ManaSymbol';

const GridContainer = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(1),
  alignItems: 'center',
  justifyItems: 'center',
}));

const ExtendedSearchForm: React.FC = () => {
  const { cardFilter, setCardFilter, loadCards } = useContext(CardFilterContext);
  const { supertypeList, typeList, subtypeList } = useContext(CardTypesContext);

  const [open, setOpen] = useState<boolean>(false);
  const [editedFilter, setEditedFilter] = useState<CardFilter>(defaultCardFilter);
  const [loading, setLoading] = useState<boolean>(false);
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);

  const gridWidth = {
    xs: 12,
    sm: 2,
  };

  const fuzzySearchCardNames = useCallback(
    async (name: string) => {
      if (name === '') {
        setNameSuggestions([]);
        return;
      }
      setLoading(true);
      const result: FuzzySearchResult = await fetchCardNames(name);
      const newNameSuggestions: string[] = result.language === 'ja'
        ? result.card_names.map(card_name => card_name.ja_name)
        : result.card_names.map(card_name => card_name.original_name)
      setNameSuggestions(newNameSuggestions);
      setLoading(false);

    }, [],
  )

  useEffect(
    () => setEditedFilter(cardFilter), [],
  );

  const handleClick = () => {
    setOpen(!open);
  };
  const handleClickSearch = () => {
    setOpen(false);
    const newCardFilter: CardFilter = { ...editedFilter, page: 1 };
    setCardFilter(newCardFilter);
    loadCards(newCardFilter);
  };
  const renderSearchBox = (
    <Stack alignItems='center'>
      <Paper
        component="form"
        noValidate
        autoComplete="off"
        sx={{ display: 'flex', alignItems: 'center', margin: 1, width: '100%' }}
      >
        <Autocomplete
          fullWidth
          freeSolo
          id='name'
          loading={loading}
          value={editedFilter.name_or_text}
          inputValue={editedFilter.name_or_text}
          options={nameSuggestions}
          filterOptions={(options) => options}
          onChange={(_, value) => {
            if (value === null) {
              setCardFilter({
                ...editedFilter,
                name_or_text: '',
              });
              setNameSuggestions([]);
            } else {
              const newCardFilter: CardFilter = {
                ...defaultCardFilter,
                name_or_text: value ?? '',
              }
              setOpen(false);
              setCardFilter(newCardFilter);
              setNameSuggestions([]);
              loadCards(newCardFilter);
            }
          }}
          onInputChange={(_, value) => {
            setEditedFilter({
              ...editedFilter,
              name_or_text: value,
            });
            if (editedFilter.name_search === true) {
              fuzzySearchCardNames(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={editedFilter.name_search ? 'カード名' : 'テキスト'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleClickSearch();
                };
              }}
            />
          )}
        />
        <IconButton onClick={handleClickSearch} sx={{ m: 1 }}>
          <SearchIcon />
        </IconButton>
      </Paper>
      <RadioGroup
        row
        value={editedFilter.name_search}
        onChange={() => {
          const switched: boolean = !editedFilter.name_search;
          setEditedFilter({
            ...editedFilter,
            name_search: switched,
          })
          if (switched === true) {
            fuzzySearchCardNames(editedFilter.name_or_text);
          } else {
            setNameSuggestions([]);
          }
        }}
      >
        <FormControlLabel value={true} control={<Radio />} label="カード名" />
        <FormControlLabel value={false} control={<Radio />} label="テキスト" />
      </RadioGroup>
    </Stack>
  )

  const renderColors = (
    <GridContainer container>
      <Grid item xs={gridWidth.xs} sm={gridWidth.sm}>
        <Button
          onClick={() => setEditedFilter({
            ...editedFilter,
            w: defaultCardFilter.w,
            u: defaultCardFilter.u,
            b: defaultCardFilter.b,
            r: defaultCardFilter.r,
            g: defaultCardFilter.g,
            colorless: defaultCardFilter.colorless,
            multicolored: defaultCardFilter.multicolored,
            colorIdentity: defaultCardFilter.colorIdentity
          })}
          endIcon={<RotateLeftIcon />}
        >
          色
        </Button>
      </Grid>
      <Grid item xs sm>
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, w: !editedFilter.w })}
          src={cardSymbol.w}
          disable={editedFilter.w === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, u: !editedFilter.u })}
          src={cardSymbol.u}
          disable={editedFilter.u === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, b: !editedFilter.b })}
          src={cardSymbol.b}
          disable={editedFilter.b === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, r: !editedFilter.r })}
          src={cardSymbol.r}
          disable={editedFilter.r === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, g: !editedFilter.g })}
          src={cardSymbol.g}
          disable={editedFilter.g === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, colorless: !editedFilter.colorless })}
          src={cardSymbol.colorless}
          disable={editedFilter.colorless === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, multicolored: !editedFilter.multicolored })}
          src={cardSymbol.multicolored}
          disable={editedFilter.multicolored === false}
        />
        <FormControlLabel
          sx={{ m: 1 }}
          control={
            <Checkbox
              checked={editedFilter.colorIdentity}
              onChange={() => setEditedFilter({ ...editedFilter, colorIdentity: !editedFilter.colorIdentity })}
            />
          }
          label="固有色で探す"
        />
      </Grid>
    </GridContainer>
  );

  const renderManaCosts = (
    <GridContainer container>
      <Grid item xs={gridWidth.xs} sm={gridWidth.sm}>
        <Button
          onClick={() => setEditedFilter({
            ...editedFilter,
            cost0: defaultCardFilter.cost0,
            cost1: defaultCardFilter.cost1,
            cost2: defaultCardFilter.cost2,
            cost3: defaultCardFilter.cost3,
            cost4: defaultCardFilter.cost4,
            cost5: defaultCardFilter.cost5,
            cost6: defaultCardFilter.cost6,
            cost7: defaultCardFilter.cost7,
            cost8: defaultCardFilter.cost8,
            cost9: defaultCardFilter.cost9,
            costOver10: defaultCardFilter.costOver10,
          })}
          endIcon={<RotateLeftIcon />}
        >
          マナ総量
        </Button>
      </Grid>
      <Grid item xs sm>

        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost0: !editedFilter.cost0 })}
          src={cardSymbol.cost0}
          disable={editedFilter.cost0 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost1: !editedFilter.cost1 })}
          src={cardSymbol.cost1}
          disable={editedFilter.cost1 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost2: !editedFilter.cost2 })}
          src={cardSymbol.cost2}
          disable={editedFilter.cost2 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost3: !editedFilter.cost3 })}
          src={cardSymbol.cost3}
          disable={editedFilter.cost3 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost4: !editedFilter.cost4 })}
          src={cardSymbol.cost4}
          disable={editedFilter.cost4 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost5: !editedFilter.cost5 })}
          src={cardSymbol.cost5}
          disable={editedFilter.cost5 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost6: !editedFilter.cost6 })}
          src={cardSymbol.cost6}
          disable={editedFilter.cost6 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost7: !editedFilter.cost7 })}
          src={cardSymbol.cost7}
          disable={editedFilter.cost7 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost8: !editedFilter.cost8 })}
          src={cardSymbol.cost8}
          disable={editedFilter.cost8 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, cost9: !editedFilter.cost9 })}
          src={cardSymbol.cost9}
          disable={editedFilter.cost9 === false}
        />
        <SymbolButton
          onClick={() => setEditedFilter({ ...editedFilter, costOver10: !editedFilter.costOver10 })}
          src={cardSymbol.costOver10}
          disable={editedFilter.costOver10 === false}
        />
      </Grid>
    </GridContainer>
  );

  type CardTypeAutocompleteProps = {
    label: string,
    cardTypeList: CardType[],
    selectedValue: CardType[],
    onChange: (e: React.SyntheticEvent<Element, Event>, value: CardType[]) => void,
  };
  const CardTypeAutocomplete: React.FC<CardTypeAutocompleteProps> = React.memo(
    (
      { label, cardTypeList, selectedValue, onChange }
    ) => {
      return (
        <Autocomplete
          multiple
          id={label}
          options={cardTypeList}
          getOptionLabel={(option) => `${option.ja} / ${option.en}`}
          filterOptions={(options, { inputValue }) => (
            options.filter((option) => (
              option.ja.includes(inputValue)
              || option.furigana.includes(inputValue)
              || option.en.toLowerCase().includes(inputValue.toLowerCase())
            ))
          )}
          groupBy={(option) => option.sub_category}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='standard'
              label={label}
            />
          )}
          value={selectedValue}
          onChange={onChange}
        />
      );
    });

  type CardTypeSelectProps = {
    label: string,
    cardTypeList: CardType[],
    selectedValue: CardType[],
    setCardTypes: (cardTypes: CardType[]) => void,
  };
  const CardTypeSelect: React.FC<CardTypeSelectProps> = React.memo(
    (
      { label, cardTypeList, selectedValue, setCardTypes }
    ) => {
      const chipDelete = (ID: number) => {
        setCardTypes(selectedValue.filter(v => v.ID !== ID));
      };

      return (
        <FormControl fullWidth>
          <InputLabel variant='standard'>
            {label}
          </InputLabel>
          <Select
            multiple
            variant='standard'
            fullWidth
            value={selectedValue}
            onChange={(e) => setCardTypes(e.target.value as CardType[])}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {selected.map((v) => (
                  <Chip
                    key={v.ID}
                    label={`${v.ja} / ${v.en}`}
                    onDelete={(_) => chipDelete(v.ID)}
                    onMouseDown={(e) => { e.stopPropagation() }}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
            )}
          >
            {cardTypeList.map(cardType => (
              <MenuItem key={cardType.ID} value={cardType as any}>
                {`${cardType.ja} / ${cardType.en}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    });

  const renderTypes = (
    <GridContainer container>
      <Grid item xs={gridWidth.xs} sm={gridWidth.sm}>
        <Button
          onClick={() => setEditedFilter({
            ...editedFilter,
            supertypes: defaultCardFilter.supertypes,
            types: defaultCardFilter.types,
            subtypes: defaultCardFilter.subtypes,
          })}
          endIcon={<RotateLeftIcon />}>
          タイプ行
        </Button>
      </Grid>
      <Grid item xs sm>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12}>
            <CardTypeSelect
              label='特殊タイプ'
              cardTypeList={supertypeList}
              selectedValue={editedFilter.supertypes}
              setCardTypes={(cardTypes) => setEditedFilter({
                ...editedFilter,
                supertypes: cardTypes,
              })}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <CardTypeSelect
              label='カード・タイプ'
              cardTypeList={typeList}
              selectedValue={editedFilter.types}
              setCardTypes={(cardTypes) => setEditedFilter({
                ...editedFilter,
                types: cardTypes,
              })}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <CardTypeAutocomplete
              label='サブタイプ'
              cardTypeList={subtypeList}
              selectedValue={editedFilter.subtypes}
              onChange={(_, value) => setEditedFilter({
                ...editedFilter,
                subtypes: value,
              })}
            />
          </Grid>
        </Grid>
      </Grid>
    </GridContainer>
  );

  const renderStatus = (
    <GridContainer container>
      <Grid item xs={gridWidth.xs} sm={gridWidth.sm}>
        <Button
          onClick={() => setEditedFilter({
            ...editedFilter,
            powerFr: defaultCardFilter.powerFr,
            powerTo: defaultCardFilter.powerTo,
            toughnessFr: defaultCardFilter.toughnessFr,
            toughnessTo: defaultCardFilter.toughnessTo,
          })}
          endIcon={<RotateLeftIcon />}
        >
          パワー / タフネス
        </Button>
      </Grid>
      <Grid item xs={gridWidth.xs} sm sx={{ margin: 1 }}>
        <Select
          variant='standard'
          value={editedFilter.powerFr}
          onChange={(e) => setEditedFilter({
            ...editedFilter,
            powerFr: e.target.value
          })}
          sx={{ m: 1 }}
        >
          {statusRange.map((status) => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </Select>
        ≦ パワー ≦
        <Select
          variant='standard'
          value={editedFilter.powerTo}
          onChange={(e) => setEditedFilter({
            ...editedFilter,
            powerTo: e.target.value
          })}
          sx={{ m: 1 }}
        >
          {statusRange.map((status) => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={gridWidth.xs} sm sx={{ margin: 1 }}>
        <Select
          variant='standard'
          value={editedFilter.toughnessFr}
          onChange={(e) => setEditedFilter({
            ...editedFilter,
            toughnessFr: e.target.value
          })}
          sx={{ m: 1 }}
        >
          {statusRange.map((status) => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </Select>
        ≦ タフネス ≦
        <Select
          variant='standard'
          value={editedFilter.toughnessTo}
          onChange={(e) => setEditedFilter({
            ...editedFilter,
            toughnessTo: e.target.value
          })}
          sx={{ m: 1 }}
        >
          {statusRange.map((status) => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </Select>
      </Grid>
    </GridContainer>
  );

  const renderOrder = (
    <GridContainer container>
      <Grid item xs={gridWidth.xs} sm={gridWidth.sm}>
        <Button
          onClick={() => setEditedFilter({
            ...editedFilter,
            order: defaultCardFilter.order,
            orderASC: defaultCardFilter.orderASC,
          })}
          endIcon={<RotateLeftIcon />}
        >
          並び順
        </Button>
      </Grid>
      <Select
        variant='standard'
        value={editedFilter.order.key}
        onChange={(e) => {
          const newOrder: Order = orderKeys.find(order => order.key === e.target.value) ?? defaultCardFilter.order;
          setEditedFilter({
            ...editedFilter,
            order: newOrder,
          });
        }}>
        {orderKeys.map((order) => (
          <MenuItem key={order.key} value={order.key}>{order.key}</MenuItem>
        ))}
      </Select>
      <RadioGroup
        row
        value={editedFilter.orderASC}
        onChange={() => setEditedFilter({
          ...editedFilter,
          orderASC: !editedFilter.orderASC
        })}
        sx={{ m: 1 }}
      >
        <FormControlLabel value={true} control={<Radio />} label="昇順" />
        <FormControlLabel value={false} control={<Radio />} label="降順" />
      </RadioGroup>
    </GridContainer>
  );


  return (
    <Box sx={{ m: 2, maxWidth: 640 }}>
      {renderSearchBox}
      <Collapse in={open}>
        <Box>
          {renderColors}
          {renderManaCosts}
          {renderTypes}
          {renderStatus}
          {renderOrder}
          <Stack alignItems='center'>
            <Button
              fullWidth
              size='large'
              variant='contained'
              onClick={handleClickSearch}
              endIcon={<SearchIcon />}
              sx={{ m: 1 }}
            >
              検索
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => setEditedFilter(defaultCardFilter)}
              sx={{ m: 1 }}
            >
              リセット
            </Button>
          </Stack>
        </Box>
      </Collapse>
      <Button
        fullWidth
        onClick={handleClick}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
      >
        詳細検索
      </Button>
    </Box >
  )
};

export default ExtendedSearchForm;
